// Modal opened from the dashboard sign-in screen's "Request a Demo"
// CTA. POSTs to /v1/demo-requests on the api (public endpoint, no
// auth) which emails the lead through the same Resend infra the
// alerting engine uses.
//
// Portal'd to document.body so it overlays cleanly even when the
// LoggedOutPage has its own radial backdrop. Same close-on-Escape /
// outside-click pattern as the rest of the dashboard's overlays.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { API_BASE } from '@/apiConfig';
import { cn } from '@/lib/cn';

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function RequestDemoModal({ open, onClose }: Props) {
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const honeypotRef           = useRef<HTMLInputElement>(null);
  const [status, setStatus]   = useState<Status>('idle');
  const [err, setErr]         = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset state on close so a reopen starts fresh.
  useEffect(() => {
    if (!open) {
      setStatus('idle');
      setErr(null);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'submitting' || status === 'success') return;
    setStatus('submitting');
    setErr(null);
    try {
      const res = await fetch(`${API_BASE}/demo-requests`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          name, email, company, message,
          website: honeypotRef.current?.value ?? '',
        }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.error) detail = j.error; } catch { /* */ }
        throw new Error(detail);
      }
      setStatus('success');
    } catch (e: unknown) {
      setStatus('error');
      setErr(e instanceof Error ? e.message : 'Could not send your request.');
    }
  };

  const disabled = status === 'submitting' || status === 'success';

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-label="Request a Demo"
        aria-modal="true"
        className="fixed inset-0 z-[61] grid place-items-center p-4"
        onClick={(e) => {
          // Click outside the inner card closes; clicks on the card itself
          // shouldn't bubble up to the backdrop.
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border-strong)] rounded-xl shadow-[var(--shadow-xl)] overflow-hidden">
          <header className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-subtle)]">
            <h2 className="font-display font-semibold text-base">Request a Demo</h2>
            <button
              onClick={onClose}
              aria-label="close"
              className="p-1 rounded text-[var(--fg-muted)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-elevated)] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </header>

          {status === 'success' ? (
            <div className="p-6 text-center space-y-3">
              <CheckCircle2 className="w-10 h-10 text-[var(--accent-healthy)] mx-auto" />
              <h3 className="font-display font-semibold text-lg">Thanks!</h3>
              <p className="text-sm text-[var(--fg-muted)]">
                We received your request and will reach out within one business day.
              </p>
              <button
                onClick={onClose}
                className="mt-2 px-4 py-1.5 rounded-md bg-[var(--accent-brand)] hover:bg-[var(--accent-brand-hover)] text-white text-sm font-medium transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-5 space-y-3">
              <p className="text-xs text-[var(--fg-muted)] mb-1">
                Tell us a bit about your operation and we{"'"}ll set up a walkthrough
                of the Phos Intelligence Dashboard.
              </p>

              <Field label="Name" required>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  disabled={disabled}
                  className={inputCls}
                />
              </Field>

              <Field label="Email" required>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={disabled}
                  className={inputCls}
                />
              </Field>

              <Field label="Company">
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  autoComplete="organization"
                  disabled={disabled}
                  className={inputCls}
                />
              </Field>

              <Field label="What would you like to see?" required>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={4}
                  disabled={disabled}
                  className={cn(inputCls, 'resize-y min-h-[88px]')}
                  placeholder="Number of machines, brands, current monitoring setup, etc."
                />
              </Field>

              {/* Honeypot — hidden from humans, often filled by bots. */}
              <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden>
                <label>
                  Website (leave blank)
                  <input
                    ref={honeypotRef}
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </label>
              </div>

              {err && (
                <div className="text-xs text-[var(--accent-destructive)] bg-[var(--accent-destructive)]/10 border border-[var(--accent-destructive)]/30 rounded px-2 py-1.5">
                  {err}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 rounded-md text-sm text-[var(--fg-muted)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-elevated)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={disabled}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-[var(--accent-brand)] hover:bg-[var(--accent-brand-hover)] text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {status === 'submitting' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {status === 'submitting' ? 'Sending…' : 'Send request'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

function Field({
  label, required, children,
}: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-xs text-[var(--fg-muted)] space-y-1">
      <span>
        {label}{required && <span className="text-[var(--accent-destructive)] ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full px-2.5 py-1.5 rounded-md bg-[var(--bg-base)] border border-[var(--border-strong)] text-[var(--fg-primary)] text-sm placeholder:text-[var(--fg-faint)] focus:outline-none focus:border-[var(--accent-brand)] disabled:opacity-60';
